/* YYY / Wanwan header — vanilla JS
   - Mega menu hover-intent (open on enter, ~180ms close delay) + keyboard focus
   - Mobile drawer: scroll lock + Esc close + focus management
   - Mobile search tray toggle
   - Predictive search via Shopify Predictive Search API (/search/suggest.json),
     debounced, keyboard-navigable, graceful fallback to the plain GET form.
*/
(function () {
  'use strict';

  var CLOSE_DELAY = 180;

  /* ---------------- Mega menu (desktop, hover-intent) ---------------- */
  function initMega() {
    var nav = document.querySelector('[data-mega-nav]');
    if (!nav) return;
    var items = nav.querySelectorAll('[data-mega-item]');
    var closeTimer = null;

    Array.prototype.forEach.call(items, function (item) {
      var trigger = item.querySelector('[data-mega-trigger]');

      function open() {
        if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
        Array.prototype.forEach.call(items, function (other) {
          if (other !== item) closeItem(other);
        });
        item.classList.add('is-open');
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
      }
      function scheduleClose() {
        if (closeTimer) clearTimeout(closeTimer);
        closeTimer = setTimeout(function () { closeItem(item); }, CLOSE_DELAY);
      }

      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', scheduleClose);
      // Keyboard / focus: open when focus enters, close when it leaves the item.
      item.addEventListener('focusin', open);
      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) closeItem(item);
      });
    });

    function closeItem(item) {
      item.classList.remove('is-open');
      var t = item.querySelector('[data-mega-trigger]');
      if (t) t.setAttribute('aria-expanded', 'false');
    }

    // Esc closes any open mega panel.
    nav.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        Array.prototype.forEach.call(items, closeItem);
      }
    });
  }

  /* ---------------- Mobile drawer ---------------- */
  function initDrawer() {
    var drawer = document.querySelector('[data-drawer]');
    var toggle = document.querySelector('[data-drawer-toggle]');
    if (!drawer || !toggle) return;
    var panel = drawer.querySelector('[data-drawer-panel]');
    var overlay = drawer.querySelector('[data-drawer-overlay]');
    var closeBtn = drawer.querySelector('[data-drawer-close]');
    var lastFocused = null;

    function open() {
      lastFocused = document.activeElement;
      drawer.hidden = false;
      // force reflow so the transition runs from the hidden state
      void drawer.offsetWidth;
      drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', 'Close menu');
      if (closeBtn) closeBtn.focus();
    }
    // Read the real transition duration instead of hardcoding it, so the panel
    // is never hidden mid-animation if the CSS timing changes.
    function hideDelay() {
      if (!panel) return 0;
      var declared = window.getComputedStyle(panel).transitionDuration || '0s';
      var seconds = parseFloat(declared.split(',')[0]);
      return isNaN(seconds) ? 0 : seconds * 1000;
    }

    function close() {
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Open menu');
      window.setTimeout(function () { drawer.hidden = true; }, hideDelay());
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    toggle.addEventListener('click', open);

    // The drawer is mobile-only chrome. If the viewport grows past the point
    // where the desktop nav takes over, close it rather than stranding an open
    // panel over a header that already shows its navigation.
    var desktopNav = window.matchMedia('(min-width: 990px)');
    var onBreakpoint = function (event) {
      if (event.matches && drawer.classList.contains('is-open')) close();
    };
    if (desktopNav.addEventListener) desktopNav.addEventListener('change', onBreakpoint);
    else if (desktopNav.addListener) desktopNav.addListener(onBreakpoint);

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    // Simple focus trap within the panel.
    if (panel) {
      panel.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab') return;
        var focusable = panel.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])');
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
    }

    // Collapsible groups inside the drawer.
    var groups = drawer.querySelectorAll('[data-drawer-group-toggle]');
    Array.prototype.forEach.call(groups, function (btn) {
      btn.addEventListener('click', function () {
        var sub = btn.parentElement.querySelector('.mobile-drawer__sub');
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        if (sub) sub.hidden = expanded;
      });
    });
  }

  /* ---------------- Mobile search tray ---------------- */
  function initMobileSearch() {
    var toggle = document.querySelector('[data-mobile-search-toggle]');
    var tray = document.querySelector('[data-mobile-search]');
    if (!toggle || !tray) return;
    var iconSearch = toggle.querySelector('.icon-search');
    var iconClose = toggle.querySelector('.icon-close');

    toggle.addEventListener('click', function () {
      var willShow = tray.hidden;
      tray.hidden = !willShow;
      toggle.setAttribute('aria-expanded', String(willShow));
      toggle.setAttribute('aria-label', willShow ? 'Close search' : 'Search');
      if (iconSearch) iconSearch.hidden = willShow;
      if (iconClose) iconClose.hidden = !willShow;
      if (willShow) {
        var input = tray.querySelector('[data-predictive-search-input]');
        slog('mobile search tray opened', { 'field found': !!input });
        if (input) input.focus();
      }
    });
  }

  /* ---------------- Predictive search ---------------- */
  /* Shared config lives on the Search section, which renders once per page. */
  function settingsAttr(name) {
    var el = document.querySelector('[data-search-settings]');
    return el ? el.getAttribute(name) : '';
  }

  /* ---------------- Search debug channel ----------------
     Every step of the search path logs under one prefix, so "the zero-results
     recovery never fires" becomes a question the console answers directly:
     was the section rendered, did Liquid resolve a collection, how many cards
     did it stage, and did the no-match branch actually run.

     OFF in production — nothing is printed. To diagnose the search path again,
     either flip this to true or, without touching the theme, run this in the
     browser console and reopen the search field:

       window.YYY_SEARCH_DEBUG = true
  */
  var SEARCH_DEBUG = false;

  function searchDebugOn() {
    return SEARCH_DEBUG || window.YYY_SEARCH_DEBUG === true;
  }

  function slog(label, data) {
    if (!searchDebugOn() || !window.console) return;
    if (arguments.length > 1) console.log('%c[YYY search]', 'color:#5E7E12;font-weight:700', label, data);
    else console.log('%c[YYY search]', 'color:#5E7E12;font-weight:700', label);
  }

  function swarn(label, data) {
    if (!searchDebugOn() || !window.console) return;
    if (arguments.length > 1) console.warn('%c[YYY search]', 'color:#B27A1A;font-weight:700', label, data);
    else console.warn('%c[YYY search]', 'color:#B27A1A;font-weight:700', label);
  }

  /* One-time report on what the server actually shipped. Runs before any typing,
     so a misconfigured recovery collection is visible on page load rather than
     only after someone happens to search for something that misses. */
  function reportSearchConfig() {
    if (!searchDebugOn()) return;
    var el = document.querySelector('[data-search-settings]');
    if (!el) {
      swarn('recovery section NOT in the DOM — layout/theme.liquid is not rendering {% section "search-settings" %}, so the dropdown has no recovery cards to show.');
      return;
    }
    var tpl = el.querySelector('[data-zero-search-predictive-template]');
    var staged = tpl && tpl.content ? tpl.content.children.length : 0;
    slog('recovery config', {
      'Recovery collection setting': el.getAttribute('data-zero-setting-raw'),
      'Resolved from': el.getAttribute('data-zero-source'),
      'Resolved collection': el.getAttribute('data-zero-title'),
      'Collection URL': el.getAttribute('data-zero-url'),
      'Dropdown heading': el.getAttribute('data-zero-heading'),
      'Products in collection': el.getAttribute('data-zero-products-count'),
      'Cards staged by Liquid': el.getAttribute('data-zero-staged-count'),
      'Cards found in <template>': staged
    });
    if (staged === 0) {
      swarn('0 recovery cards staged. The dropdown will show the no-match message with nothing under it. Check Theme settings > Search > Recovery collection, and that the collection has products.');
    }
    slog('search widgets on page', document.querySelectorAll('[data-predictive-search]').length);

    /* The full search page renders its own zero-state server-side, so it never
       touches the code above. Reported here too, otherwise "it never fires" is
       ambiguous between the dropdown and the page. */
    var pageZero = document.querySelector('[data-search-zero]');
    if (pageZero) {
      slog('search PAGE zero-state rendered', {
        'Query': pageZero.getAttribute('data-search-zero-terms'),
        'Recovery collection': pageZero.getAttribute('data-search-zero-collection'),
        'Cards rendered': pageZero.getAttribute('data-search-zero-cards')
      });
    }
  }

  /* The placeholder and the full zero-results page are rendered server-side
     from Theme settings > Search now, so nothing here has to patch the DOM on
     load. Only the suggestions dropdown still reads staged markup, and it does
     that at query time in render(). */

  function initPredictiveSearch() {
    var widgets = document.querySelectorAll('[data-predictive-search]');
    Array.prototype.forEach.call(widgets, function (widget) {
      var input = widget.querySelector('[data-predictive-search-input]');
      var results = widget.querySelector('[data-predictive-search-results]');
      var listbox = widget.querySelector('[data-predictive-search-listbox]');
      var message = widget.querySelector('[data-predictive-search-message]');
      var status = widget.querySelector('[data-predictive-search-status]');
      var clear = widget.querySelector('[data-predictive-search-clear]');
      var form = widget.querySelector('form');
      if (!input || !results || !listbox || !form) return;

      /* Guard against a second init pass (Shopify re-renders sections in design
         mode) binding a duplicate set of listeners to the same widget. */
      if (widget.hasAttribute('data-predictive-search-ready')) return;
      widget.setAttribute('data-predictive-search-ready', '');

      var debounce = null;
      var activeIndex = -1;
      var lastQuery = '';
      var requestId = 0;

      function announce(text) {
        if (status) status.textContent = text || '';
      }

      function setExpanded(state) {
        results.hidden = !state;
        input.setAttribute('aria-expanded', String(!!state));
        if (!state) {
          input.removeAttribute('aria-activedescendant');
          activeIndex = -1;
        }
      }

      function setMessage(text) {
        if (!message) return;
        message.textContent = text || '';
        message.hidden = !text;
      }

      function hide() {
        setExpanded(false);
        listbox.innerHTML = '';
        setMessage('');
        listbox.removeAttribute('aria-busy');
      }

      function syncClear() {
        if (clear) clear.hidden = input.value.length === 0;
      }

      /* Busy state only; the previous suggestions stay on screen. Wiping the
         panel on every keystroke made the list flicker and the panel jump, and
         it threw away the results the shopper was in the middle of reading. */
      function showLoading() {
        listbox.setAttribute('aria-busy', 'true');
        if (!listbox.children.length) setMessage('Searching…');
        setExpanded(true);
        announce('Searching');
      }

      function collectItems() {
        return listbox.querySelectorAll('[data-ps-item]');
      }

      /* Assigns option ids and resets selection. Both render paths need it, so
         it lives here rather than being duplicated at the end of each. */
      function indexItems() {
        Array.prototype.forEach.call(collectItems(), function (item, index) {
          item.id = listbox.id + '-option-' + index;
          item.setAttribute('aria-selected', 'false');
        });
        activeIndex = -1;
      }

      function zeroTemplateEl() {
        return document.querySelector('[data-zero-search-predictive-template]');
      }

      function groupOpen(label) {
        return '<div role="group" aria-label="' + escapeHtml(label) + '">'
          + '<p class="predictive-search__group-label" aria-hidden="true">' + escapeHtml(label) + '</p>';
      }

      /* ---- Zero-query state ----
         Shown the moment the field opens, before a single character is typed.
         A focused, empty search box is the point at which a shopper is least
         sure what to ask for, so the panel offers the merchant's picks instead
         of nothing. https://baymard.com/blog/search-suggestions

         Returns true when it actually rendered, so callers can fall back to
         closing the panel when the state is disabled or has no products. */
      function renderZeroQuery(reason) {
        if (settingsAttr('data-empty-enabled') === 'false') {
          slog('zero-query state SKIPPED — turned off in the Search suggestions section');
          return false;
        }
        var tpl = zeroTemplateEl();
        if (!tpl) {
          swarn('zero-query state SKIPPED — [data-zero-search-predictive-template] is missing; the search-settings section did not render.');
          return false;
        }
        if (!tpl.content.children.length) {
          swarn('zero-query state SKIPPED — the staged <template> is empty. Set "Products to show" in the Search suggestions section.');
          return false;
        }

        var heading = settingsAttr('data-empty-heading') || 'Popular picks';
        var url = settingsAttr('data-zero-url');
        var title = settingsAttr('data-zero-title');
        var html = groupOpen(heading) + tpl.innerHTML + '</div>';
        if (url) {
          html += '<a class="predictive-search__all" data-ps-item role="option" href="' + escapeHtml(url) + '">'
            + 'Browse ' + escapeHtml(title || 'all products')
            + '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>'
            + '</a>';
        }

        setMessage('');
        listbox.innerHTML = html;
        listbox.removeAttribute('aria-busy');
        indexItems();
        setExpanded(true);
        /* Not announced as a result count — nothing was searched for. The
           combobox is simply reporting that suggestions are available. */
        announce(heading + ' available. Type to search.');
        slog('zero-query state RENDERED (' + reason + ')', {
          heading: heading,
          cards: tpl.content.children.length,
          collection: title
        });
        return true;
      }

      function setActive(index) {
        var items = collectItems();
        if (!items.length) return;
        if (index < 0) index = items.length - 1;
        if (index >= items.length) index = 0;
        Array.prototype.forEach.call(items, function (el, i) {
          el.classList.toggle('is-active', i === index);
          el.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });
        activeIndex = index;
        input.setAttribute('aria-activedescendant', items[index].id);
        items[index].scrollIntoView({ block: 'nearest' });
      }

      function money(cents) {
        try {
          if (window.Shopify && window.Shopify.currency) {
            return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: window.Shopify.currency.active });
          }
        } catch (e) { /* fall through */ }
        return (cents / 100).toFixed(2);
      }

      function render(data, query) {
        var products = (data.resources && data.resources.results && data.resources.results.products) || [];
        var collections = (data.resources && data.resources.results && data.resources.results.collections) || [];
        var pages = (data.resources && data.resources.results && data.resources.results.pages) || [];
        var html = '';

        /* Options may only sit inside the listbox or a role="group" within it,
           so every visual group label is wrapped and hidden from AT — the group
           carries the name instead. Shared with the zero-query state as
           groupOpen(). */
        var openGroup = groupOpen;

        if (collections.length) {
          html += openGroup('Categories');
          collections.slice(0, 2).forEach(function (c) {
            html += '<a class="predictive-search__result" data-ps-item role="option" href="' + escapeHtml(c.url) + '">'
              /* escaped, matching the product branch below — an unescaped URL
                 interpolated into an attribute is an injection point. */
              + (c.featured_image && c.featured_image.url ? '<img src="' + escapeHtml(c.featured_image.url) + '" alt="" width="40" height="40" loading="lazy">' : '')
              + '<span class="predictive-search__result-body">'
              + '<span class="predictive-search__result-title">' + escapeHtml(c.title) + '</span>'
              + '<span class="predictive-search__result-meta">Collection</span>'
              + '</span></a>';
          });
          html += '</div>';
        }

        if (products.length) {
          html += openGroup('Products');
          products.slice(0, 4).forEach(function (p) {
            var img = p.featured_image && p.featured_image.url ? p.featured_image.url : (p.image || '');
            var price = (typeof p.price_min !== 'undefined') ? money(p.price_min) : (p.price ? money(p.price) : '');
            var meta = [p.vendor, (p.type || '')].filter(Boolean).join(' · ');
            html += '<a class="predictive-search__result" data-ps-item role="option" href="' + escapeHtml(p.url) + '">'
              /* Products with no image keep the same 40px slot, so titles in a
                 mixed list stay on one left edge instead of stepping. */
              + (img
                ? '<img src="' + escapeHtml(img) + '" alt="" width="40" height="40" loading="lazy">'
                : '<span class="predictive-search__result-noimage" aria-hidden="true"></span>')
              + '<span class="predictive-search__result-body">'
              + '<span class="predictive-search__result-title">' + escapeHtml(p.title) + '</span>'
              + (meta ? '<span class="predictive-search__result-meta">' + escapeHtml(meta) + '</span>' : '')
              + '</span>'
              // + (price ? '<span class="predictive-search__result-price">' + escapeHtml(price) + '</span>' : '')
              + '</a>';
          });
          html += '</div>';
        }

        if (pages.length) {
          html += openGroup('Help & information');
          pages.slice(0, 2).forEach(function (page) {
            html += '<a class="predictive-search__result predictive-search__result--page" data-ps-item role="option" href="' + escapeHtml(page.url) + '">'
              + '<span class="predictive-search__result-body">'
              + '<span class="predictive-search__result-title">' + escapeHtml(page.title) + '</span>'
              + '<span class="predictive-search__result-meta">Page</span>'
              + '</span></a>';
          });
          html += '</div>';
        }

        var matched = products.length + collections.length + pages.length;

        slog('results for "' + query + '"', {
          products: products.length,
          collections: collections.length,
          pages: pages.length,
          total: matched,
          branch: matched ? 'MATCHED — no recovery needed' : 'ZERO — recovery branch'
        });

        if (!matched) {
          /* Never a bare dead end: state the miss, then offer the merchant's
             recovery products. https://baymard.com/blog/no-results-page */
          setMessage('No matches for “' + query + '”. Try a broader term, or browse these picks.');
          var zeroTemplate = zeroTemplateEl();
          if (!zeroTemplate) {
            swarn('zero-results branch fired but [data-zero-search-predictive-template] is missing — the search-settings section did not render.');
          } else if (!zeroTemplate.content.children.length) {
            swarn('zero-results branch fired but the recovery <template> is empty — no products resolved. See the "recovery config" log above.');
          } else {
            var zeroHeading = settingsAttr('data-zero-heading') || 'Popular picks';
            html += openGroup(zeroHeading)
              + zeroTemplate.innerHTML
              + '</div>';
            slog('recovery FIRED', {
              heading: zeroHeading,
              cards: zeroTemplate.content.children.length,
              collection: settingsAttr('data-zero-title')
            });
          }
        } else {
          setMessage('');
        }

        var searchUrl = form.getAttribute('action') + '?q=' + encodeURIComponent(query);
        html += '<a class="predictive-search__all" data-ps-item role="option" href="' + searchUrl + '">'
          + 'See all results for &ldquo;' + escapeHtml(query) + '&rdquo;'
          + '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>'
          + '</a>';

        listbox.innerHTML = html;
        indexItems();
        listbox.removeAttribute('aria-busy');
        setExpanded(true);
        announce(matched
          ? matched + (matched === 1 ? ' suggestion' : ' suggestions') + ' for ' + query
          : 'No matches for ' + query);
      }

      function query(term) {
        var currentRequest = ++requestId;
        var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
        var url = root + 'search/suggest.json?q=' + encodeURIComponent(term)
          + '&resources[type]=product,collection,page'
          + '&resources[limit]=6'
          + '&resources[options][unavailable_products]=last';
        slog('querying suggest.json', { term: term, url: url });
        fetch(url, { headers: { 'Accept': 'application/json' } })
          .then(function (r) {
            if (!r.ok) throw new Error('Search request failed');
            return r.json();
          })
          .then(function (data) {
            if (currentRequest !== requestId || input.value.trim() !== term) {
              slog('response discarded (stale — the field moved on)', term);
              return;
            }
            render(data, term);
          })
          .catch(function (err) {
            swarn('suggest.json request failed', err && err.message ? err.message : err);
            /* A failed request must not strand the field in a permanent busy
               state — clear it, say so, and leave the plain form usable. */
            if (currentRequest !== requestId) return;
            listbox.innerHTML = '';
            listbox.removeAttribute('aria-busy');
            setMessage('Suggestions are unavailable right now. Press Search to run the full search.');
            setExpanded(true);
            announce('Suggestions are unavailable. Press Search to run the full search.');
          });
      }

      input.addEventListener('input', function () {
        var term = input.value.trim();
        syncClear();
        /* Below the 2-character API threshold the panel falls back to the
           zero-query picks rather than snapping shut. Deleting back to an empty
           field used to blank the dropdown, which read as the feature breaking. */
        if (term.length < 2) {
          requestId += 1;
          lastQuery = '';
          if (!renderZeroQuery(term.length ? 'typed ' + term.length + ' char, below threshold' : 'field emptied')) hide();
          return;
        }
        if (term === lastQuery) return;
        lastQuery = term;
        if (debounce) clearTimeout(debounce);
        showLoading();
        debounce = setTimeout(function () { query(term); }, 220);
      });

      if (clear) {
        clear.addEventListener('click', function () {
          input.value = '';
          lastQuery = '';
          requestId += 1;
          if (debounce) clearTimeout(debounce);
          syncClear();
          announce('Search cleared');
          /* Clearing returns to the zero-query picks, not to a blank panel —
             the field is still focused, so the shopper still needs somewhere to
             go next. */
          if (!renderZeroQuery('cleared')) hide();
          input.focus();
        });
      }

      /* Opening the field is the whole point of the zero-query state, and the
         moment worth logging: it reports the resolved config at the instant the
         shopper is about to type, rather than whatever was true at page load
         (the theme editor re-renders sections underneath us). */
      input.addEventListener('focus', function () {
        var el = document.querySelector('[data-search-settings]');
        var tpl = zeroTemplateEl();
        slog('search OPENED — ' + (input.id || 'unnamed'), {
          'Current value': input.value || '(empty)',
          'Zero-query state': el ? (el.getAttribute('data-empty-enabled') === 'false' ? 'OFF' : 'ON') : 'no section',
          'Section present': !!el,
          'Cards ready': tpl && tpl.content ? tpl.content.children.length : 0,
          'Collection': el ? el.getAttribute('data-zero-title') : '(none)'
        });

        var term = input.value.trim();
        if (term.length >= 2) {
          if (listbox.children.length) setExpanded(true);
          return;
        }
        renderZeroQuery('field focused');
      });

      input.addEventListener('keydown', function (e) {
        /* Escape closes the popup first; a second press clears the field, which
           is the combobox pattern's expected two-step. */
        if (e.key === 'Escape') {
          if (!results.hidden) { hide(); return; }
          if (input.value !== '') {
            input.value = '';
            lastQuery = '';
            requestId += 1;
            syncClear();
            announce('Search cleared');
          }
          return;
        }
        if (results.hidden) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIndex + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIndex - 1); }
        else if (e.key === 'Enter') {
          var items = collectItems();
          if (activeIndex >= 0 && items[activeIndex]) { e.preventDefault(); window.location.href = items[activeIndex].getAttribute('href'); }
        }
      });

      document.addEventListener('click', function (e) {
        /* Collapse without discarding the list, so returning to the field
           restores the suggestions instead of forcing a re-query. */
        if (!widget.contains(e.target)) setExpanded(false);
      });
      syncClear();
    });
  }

  /* ---------------- Sticky header height ---------------- */
  /* The collection toolbar and the desktop filter rail stick underneath the
     header, and the header's height is not a constant: it moves with the
     merchant's logo-height setting, with the store-name-on-mobile toggle, and
     with the mobile search tray opening. Publishing the measured height keeps
     those offsets honest; the CSS keeps its old literals as fallbacks so a JS
     failure degrades to today's behaviour rather than to zero. */
  function initHeaderHeight() {
    var header = document.querySelector('[data-site-header]');
    if (!header) return;

    function measure() {
      var height = header.classList.contains('site-header--sticky') ? header.offsetHeight : 0;
      document.documentElement.style.setProperty('--header-height', height + 'px');
    }

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    if (window.ResizeObserver) new window.ResizeObserver(measure).observe(header);
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function init() {
    initHeaderHeight();
    initMega();
    initDrawer();
    initMobileSearch();
    initPredictiveSearch();
    reportSearchConfig();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify replaces a section in place while a merchant edits its settings.
  // Editing Search re-renders the settings block; editing Header re-renders the
  // search fields themselves. Either way the shared configuration has to be
  // reapplied and any freshly rendered widget wired up — initPredictiveSearch
  // skips widgets it has already bound, so this cannot double-bind listeners.
  document.addEventListener('shopify:section:load', function (e) {
    slog('shopify:section:load — rewiring', e.detail && e.detail.sectionId ? e.detail.sectionId : '(unknown section)');
    initPredictiveSearch();
    reportSearchConfig();
  });
})();
